import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import FloatingCartIcon from "../../components/frontend/cart/FloatingCartIcon";
import Footer from "../../components/frontend/footer/Footer";
import MobileNav from "../../components/frontend/navbar/MobileNav";
import Navigation from "../../components/frontend/navbar/Navigation";
import FloatingSocialMedia from "../../components/frontend/socialpopup/FloatingSocialMedia";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import "../../styles/layout.css";

type Crumb = { label: string; linkPath?: string };

const PageLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const { t: myTranslate } = useTranslation();

  const getBreadcrumbs = (): Crumb[] => {
    const fromSearch = (
      location.state as {
        fromSearch?: { query: string; retailer?: string };
      } | null
    )?.fromSearch;

    if (location.pathname.startsWith("/product/") && fromSearch?.query) {
      const retailer = fromSearch.retailer || "amazon";
      const searchUrl = `/search?query=${encodeURIComponent(fromSearch.query)}&retailer=${encodeURIComponent(retailer)}`;
      return [
        { label: myTranslate("breadcrumbs.product") },
        { label: fromSearch.query, linkPath: searchUrl },
      ];
    }

    if (location.pathname === "/search" && location.search) {
      const searchParams = new URLSearchParams(location.search);
      const query = searchParams.get("query");
      return [
        { label: myTranslate("breadcrumbs.search") },
        {
          label: query
            ? decodeURIComponent(query)
            : myTranslate("breadcrumbs.searchResults"),
          linkPath: location.pathname + location.search,
        },
      ];
    }

    const paths = location.pathname.split("/").filter(Boolean);
    return paths.map((path) => {
      const translationKey = `breadcrumbs.${path.toLowerCase()}`;
      const translated = myTranslate(translationKey);
      let label =
        translated !== translationKey
          ? translated
          : path.charAt(0).toUpperCase() + path.slice(1);
      if (path.match(/^[A-Za-z0-9]{8,}$/)) {
        label = myTranslate("breadcrumbs.product");
      }
      return { label };
    });
  };

  const showBreadcrumbs = location.pathname !== "/";
  const useWhiteBackground =
    location.pathname === "/" || location.pathname.startsWith("/search");
  const isSearchPage = location.pathname === "/search";
  const isCartPage = location.pathname === "/cart";

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        "@supports (height: 100dvh)": {
          minHeight: "100dvh",
        },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          backgroundColor: "#FFFFFF",
        }}
      >
        <Navigation />
        {showBreadcrumbs && (
          <Box
            data-breadcrumbs
            sx={{
              backgroundColor: "#FFFFFF",
              borderBottom: "1px solid #E2E8F0",
              py: 1.25,
            }}
          >
            <Box
              sx={{
                maxWidth: "xl",
                mx: "auto",
                px: 2,
              }}
            >
              <Breadcrumbs
                aria-label="breadcrumb"
                separator="/"
                className="notranslate"
                translate="no"
                sx={{
                  fontSize: 13,
                  color: "#1a73e8",
                  "& .MuiBreadcrumbs-separator": {
                    mx: 1,
                    fontSize: 13,
                    color: "#5f6368",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#5f6368",
                    fontSize: 13,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  {myTranslate("breadcrumbs.home")}
                </Typography>

                {getBreadcrumbs().map((crumb, index, arr) => {
                  const isLast = index === arr.length - 1;
                  if (isLast && crumb.linkPath) {
                    return (
                      <Link
                        key={`${crumb.label}-${index}`}
                        component="button"
                        onClick={() => navigate(crumb.linkPath!)}
                        underline="hover"
                        sx={{
                          color: "#1a73e8",
                          cursor: "pointer",
                          textDecorationThickness: "1px",
                          lineHeight: 1.6,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {crumb.label}
                      </Link>
                    );
                  }

                  return (
                    <Typography
                      key={`${crumb.label}-${index}`}
                      sx={{
                        color: "#5f6368",
                        fontSize: 13,
                        fontWeight: 400,
                        lineHeight: 1.6,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {crumb.label}
                    </Typography>
                  );
                })}
              </Breadcrumbs>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: useWhiteBackground ? "#ffffff" : "#f5f5f5",
          p: { xs: 2, sm: 2.5, md: 3, lg: 3 },
          pt: isCartPage ? { xs: 0, sm: 0, md: 3, lg: 3 } : isSearchPage ? { xs: 1.5, sm: 2, md: 2, lg: 2 } : undefined,
          pb: {
            xs: "calc(56px + env(safe-area-inset-bottom, 0px) + 12px)",
            sm: 3,
            md: 3,
            lg: 3,
          },
        }}
      >
        <Outlet />
      </Box>

      <Footer />

      {isMobile && <MobileNav />}
      {!isMobile && <FloatingCartIcon onClick={() => navigate("/cart")} />}
      <FloatingSocialMedia />
    </Box>
  );
};

export default PageLayout;
